/**
 * Forgot Password Page
 *
 * Page for users to request a password reset link
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../../services/auth';
import { useToast } from '../../hooks/useToast';
import type { ForgotPasswordData } from '../../types';
import SEO from '../../components/seo/SEO';

export default function ForgotPasswordPage() {
  const { t, i18n } = useTranslation('auth');
  const toastify = useToast();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError(t('forgot_password.error_required'));
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const data: ForgotPasswordData = { email };
      await authService.forgotPassword(data);

      setEmailSent(true);
      toastify.success(t('forgot_password.toast_success'));
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || t('forgot_password.toast_error');
      setError(errorMessage);
      toastify.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state - show confirmation
  if (emailSent) {
    return (
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
          <svg
            className="h-6 w-6 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('forgot_password.success_title')}</h2>
        <p className="text-gray-600 mb-2">
          {t('forgot_password.success_sent_to')}
        </p>
        <p className="font-medium text-gray-900 mb-6">{email}</p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
          <h3 className="text-sm font-medium text-blue-900 mb-2">{t('forgot_password.success_next_steps')}</h3>
          <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
            <li>{t('forgot_password.success_step_1')}</li>
            <li>{t('forgot_password.success_step_2')}</li>
            <li>{t('forgot_password.success_step_3')}</li>
          </ol>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          {t('forgot_password.success_expiry')}
        </p>
        <div className="space-y-3">
          <button
            onClick={() => {
              setEmailSent(false);
              setEmail('');
            }}
            className="btn btn-secondary w-full"
          >
            {t('forgot_password.send_another')}
          </button>
          <Link to={`/${i18n.language}/login`} className="block text-sm text-indigo-600 hover:text-indigo-500">
            {t('forgot_password.back_to_login')}
          </Link>
        </div>
      </div>
    );
  }

  // Form state - request password reset
  return (
    <>
      <SEO
        title="Forgot Password | Axetra"
        description="Reset your Axetra account password."
        noindex={true}
      />
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gradient mb-2">{t('forgot_password.title')}</h2>
        <p className="text-gray-600">{t('forgot_password.description')}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div>
          <label htmlFor="email" className="label">
            {t('forgot_password.email_label')}
          </label>
          <div className="mt-1">
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="you@example.com"
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn btn-primary w-full"
          >
            {isSubmitting ? t('forgot_password.submitting') : t('forgot_password.submit_button')}
          </button>
        </div>
      </form>

      <div className="mt-6">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">{t('forgot_password.or')}</span>
          </div>
        </div>

        <div className="mt-6 text-center space-y-2">
          <Link
            to={`/${i18n.language}/login`}
            className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
          >
            {t('forgot_password.back_to_login')}
          </Link>
          <div className="text-sm text-gray-600">
            {t('forgot_password.no_account')}{' '}
            <Link to={`/${i18n.language}/register`} className="font-medium text-indigo-600 hover:text-indigo-500">
              {t('forgot_password.signup_link')}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
